import { ThemeProvider } from '@emotion/react'
import * as Sentry from '@sentry/browser'
import React from 'react'

import pkg from './package.json'
import { AppContextProvider } from './src/components/context/app-context'
import { ContactContextProvider } from './src/components/context/contact-context'
import { UserContextProvider } from './src/components/context/user-context'

import { theme, SENTRY_DSN, RECAPTCHA_SITE_KEY } from './src/config'

import { QueryParamProvider } from 'use-query-params'
import { ReachAdapter } from 'use-query-params/adapters/reach'

// GA Fix Utility Functions
function getParam(p) {
  const match = RegExp('[?&]' + p + '=([^&]*)').exec(window.location.search)
  return match && decodeURIComponent(match[1].replace(/\+/g, ' '))
}

// Save GCLID to cookie for later use
function setCookie(name, value, days) {
  const date = new Date()
  date.setDate(date.getDate() + days)
  const expiryString = date.toUTCString()
  document.cookie = `${name}=${value};expires=${expiryString};path=/`
}

export const onClientEntry = () => {
  if (process.env.NODE_ENV === 'production') {
    Sentry.init({
      dsn: SENTRY_DSN,
      release: `${pkg.name}@${pkg.version}`,
      environment: process.env.GATSBY_APP_ENV,
      ignoreErrors: [
        // https://github.com/gatsbyjs/gatsby/issues/18866
        'ChunkLoadError',
        // https://github.com/getsentry/sentry-javascript/issues/2514#issuecomment-603971338
        'Timeout',
        /Minified React error #4(18|23)/i,
      ],
    })
  }

  window.dataLayer = window.dataLayer || []

  window.recaptchaObserver = new IntersectionObserver(
    (entries, observer) => {
      if (entries[0].isIntersecting) {
        const s = document.createElement('script')
        s.src = `https://www.google.com/recaptcha/api.js?render=${RECAPTCHA_SITE_KEY}`
        s.defer = true
        s.async = true
        document.head.appendChild(s)
        observer.unobserve(entries[0].target)
      }
    },
    {
      rootMargin: '-50% 0px 0px 0px',
    },
  )
}

export const onInitialClientRender = () => {
  const gclid = getParam('gclid')

  if (gclid) {
    const gclsrc = getParam('gclsrc')
    if (!gclsrc || gclsrc.indexOf('aw') !== -1) setCookie('gclid', gclid, 90)
  }
}

export const wrapRootElement = ({ element }) => (
  <QueryParamProvider adapter={ReachAdapter} enableBatching={true}>
    <UserContextProvider>
      <ThemeProvider theme={theme}>
        <AppContextProvider>
          <ContactContextProvider>{element}</ContactContextProvider>
        </AppContextProvider>
      </ThemeProvider>
    </UserContextProvider>
  </QueryParamProvider>
)
